import type { Vault } from '@/types/vault'
import { cn } from '@/utils/cn'
import { getCuratorDisplayData, getCuratorDisplayName } from '@/utils/curator'
import { type VariantProps, cva } from 'class-variance-authority'
import { CuratorImage } from '../common/curatorImage'

const imageVariants = cva('', {
  variants: {
    size: {
      sm: 'size-3 p-px',
      md: 'size-5 p-0.5',
      base: 'size-6 p-0.5',
      inherit: 'size-4 p-0.5',
    },
  },
  defaultVariants: {
    size: 'sm',
  },
})

const textVariants = cva('whitespace-nowrap leading-none', {
  variants: {
    size: {
      sm: 'text-[10px]',
      md: 'text-sm',
      base: 'text-base',
      inherit: 'text-inherit',
    },
  },
  defaultVariants: {
    size: 'sm',
  },
})

interface Props {
  curator: NonNullable<Vault['curator']>
  className?: string
}

export const CuratorInfo = ({ curator, className, size = 'sm' }: Props & VariantProps<typeof textVariants>) => {
  if (curator.name.includes('/')) {
    const { entries, displayName, canMapDisplayNames } = getCuratorDisplayData(curator)

    return (
      <div
        className={cn(
          'flex max-w-full flex-wrap items-center gap-y-1 truncate',
          size === 'sm' ? 'gap-x-2' : 'gap-x-4',
          className
        )}
      >
        {canMapDisplayNames ? (
          entries.map(({ name, displayName }, index) => (
            <div key={name + index} className="flex items-center gap-1">
              <CuratorImage curator={name} className={imageVariants({ size })} />
              <p className={cn(textVariants({ size }), 'max-w-full truncate')} title={displayName}>
                {displayName}
              </p>
            </div>
          ))
        ) : (
          <div className="flex items-center gap-1">
            {entries.map(({ name }, index) => (
              <CuratorImage key={name + index} curator={name} className={imageVariants({ size })} />
            ))}
            <p className={cn(textVariants({ size }), 'max-w-full truncate')} title={displayName}>
              {displayName}
            </p>
          </div>
        )}
      </div>
    )
  }

  const displayName = getCuratorDisplayName(curator)

  return (
    <div className={cn('flex max-w-full items-center gap-1 truncate', className)}>
      <CuratorImage curator={curator.name} className={imageVariants({ size })} />
      <p className={cn(textVariants({ size }), 'max-w-full truncate')} title={displayName}>
        {displayName}
      </p>
    </div>
  )
}
